import { BILLING_CODES, type CaseForPharmacy } from "@omony/domain";
import { Banner, Button, KeyValueList } from "@omony/ui";
import { ArrowRight, CheckCircle2, House, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { EMPTY } from "../../shared/format.js";
import { navigate } from "../../shared/hashRoute.js";
import { useCaseAction } from "../data.js";
import { euro, Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";

const CODE_KEY: Record<string, string> = {
  [BILLING_CODES.nurErsteinschaetzung]: "triage",
  [BILLING_CODES.nurVideosprechstunde]: "video",
  [BILLING_CODES.ersteinschaetzungUndVideo]: "both",
};

/** Ergebnis der ärztlichen Behandlung (A21) – nur lesend */
export function ResultStep({ c, onNext }: { c: CaseForPharmacy; onNext: () => void }) {
  const t = useT();
  return (
    <StepFrame
      eyebrow={t("console.step.ergebnis")}
      title={t("console.result.title")}
      actions={
        <>
          <Spacer />
          <Button size="lg" icon={<ArrowRight className="omony-mirror" />} onPress={onNext}>
            {t("console.result.next")}
          </Button>
        </>
      }
    >
      <div
        className={`${styles.outcome} ${c.prescriptionIssued ? styles.outcomeSuccess : styles.outcomeWarning}`}
      >
        <p className={styles.outcomeTitle}>
          {t(c.prescriptionIssued ? "console.result.prescription" : "console.result.noPrescription")}
        </p>
        {c.prescriptionIssued ? <p>{t("console.result.prescriptionHint")}</p> : null}
      </div>
      <KeyValueList
        items={[
          { key: "doc", label: t("console.result.doctor"), value: c.doctor?.displayName ?? EMPTY },
          { key: "note", label: t("console.result.note"), value: c.noteToPharmacy || EMPTY },
        ]}
      />
    </StepFrame>
  );
}

/** Abrechnung (A22): Sonderkennzeichen und Pauschale kommen vom Server */
export function BillingStep({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const intl = useIntl();
  const submit = useCaseAction<{ version: number }>(c.id, "billing/submit");
  const code = c.billing.code ?? "";
  return (
    <StepFrame
      eyebrow={t("console.step.abrechnung")}
      title={t("console.billing.stepTitle")}
      lead={t("console.billing.lead")}
      actions={
        <>
          <Spacer />
          <Button
            size="lg"
            icon={<Send />}
            onPress={() => submit.mutate({ version: c.version })}
            isDisabled={submit.isPending}
          >
            {t("console.billing.submit")}
          </Button>
        </>
      }
    >
      <KeyValueList
        items={[
          { key: "code", label: t("console.billing.code"), value: code || EMPTY, ltr: true },
          {
            key: "service",
            label: t("console.billing.service"),
            value: CODE_KEY[code] ? t(`console.billing.service.${CODE_KEY[code]}`) : EMPTY,
          },
          {
            key: "amount",
            label: t("console.billing.amount"),
            value: <span className={styles.amount}>{euro(c.billing.amountCents ?? 0, intl.locale)}</span>,
          },
          {
            key: "ins",
            label: t("console.patient.insurance"),
            value: t(`console.patient.${c.insuranceType}`),
          },
        ]}
      />
      {c.insuranceType === "pkv" ? <Banner tone="info">{t("console.billing.pkvHint")}</Banner> : null}
      <p className={styles.small}>{t("console.billing.hint")}</p>
      <ErrorBanner error={submit.error} />
    </StepFrame>
  );
}

const RETURN_SECONDS = 20;

/** Abschluss (A23) mit sichtbarem Countdown zurück zur Startseite */
export function DoneStep({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const [left, setLeft] = useState(RETURN_SECONDS);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    if (left <= 0) {
      navigate("");
      return;
    }
    const id = window.setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left, paused]);
  return (
    <StepFrame
      eyebrow={t("console.step.abrechnung")}
      title={t("console.done.title")}
      actions={
        <>
          {!paused ? (
            <Button variant="quiet" onPress={() => setPaused(true)}>
              {t("console.done.stay")}
            </Button>
          ) : null}
          <Spacer />
          <Button size="lg" icon={<House />} onPress={() => navigate("")}>
            {t("console.done.home")}
          </Button>
        </>
      }
    >
      <div className={`${styles.outcome} ${styles.outcomeSuccess}`}>
        <CheckCircle2 aria-hidden="true" size={40} />
        <p>{t(c.status === "abgeschlossen" ? "console.done.completed" : "console.done.cancelled")}</p>
      </div>
      {!paused ? (
        <p className={styles.small} role="timer" aria-live="off">
          {t("console.done.countdown", { seconds: left })}
        </p>
      ) : null}
    </StepFrame>
  );
}
